'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import GlobalSidebar from './GlobalSidebar';
import type { KpiExplain, KpiExplainSession } from '../lib/kpiExplain';
import styles from './KpiExplainSidebar.module.css';

type KpiExplainContextValue = {
  open: (session: KpiExplainSession) => void;
  close: () => void;
};

const KpiExplainContext = createContext<KpiExplainContextValue | null>(null);

export function useKpiExplain(): KpiExplainContextValue {
  const ctx = useContext(KpiExplainContext);
  if (!ctx) throw new Error('useKpiExplain must be used within KpiExplainProvider');
  return ctx;
}

function KpiExplainContent({ title, explain }: KpiExplainSession) {
  return (
    <div className={styles.stack}>
      <h2 className={styles.title}>{title}</h2>

      <p className={styles.plainEnglish}>{explain.plainEnglish}</p>

      <div className={styles.formulaBlock}>
        <p className={styles.formulaHeadline}>{explain.formulaHeadline}</p>
        {explain.formulaDetail ? (
          <p className={styles.formulaDetail}>{explain.formulaDetail}</p>
        ) : null}
      </div>

      {explain.operands.length > 0 ? (
        <section aria-labelledby="kpi-operands-heading">
          <h3 id="kpi-operands-heading" className={styles.sectionTitle}>
            Your numbers
          </h3>
          <table className={styles.operandTable}>
            <thead>
              <tr>
                <th scope="col">Term</th>
                <th scope="col">Meaning</th>
                <th scope="col">Value</th>
              </tr>
            </thead>
            <tbody>
              {explain.operands.map((row) => (
                <tr key={`${row.symbol}-${row.label}`}>
                  <td className={styles.operandSymbol}>{row.symbol}</td>
                  <td>{row.label}</td>
                  <td className={styles.operandValue}>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      <section aria-labelledby="kpi-example-heading">
        <h3 id="kpi-example-heading" className={styles.sectionTitle}>
          Worked example
        </h3>
        <p className={styles.calculation}>{explain.calculation}</p>
        <div className={styles.resultRow}>
          <span className={styles.resultLabel}>= result</span>
          <span className={styles.resultValue}>{explain.result}</span>
        </div>
      </section>

      {explain.notes && explain.notes.length > 0 ? (
        <section aria-labelledby="kpi-notes-heading">
          <h3 id="kpi-notes-heading" className={styles.sectionTitle}>
            Good to know
          </h3>
          <ul className={styles.notes}>
            {explain.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

export function KpiExplainProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<KpiExplainSession | null>(null);

  const open = useCallback((next: KpiExplainSession) => {
    setSession(next);
  }, []);

  const close = useCallback(() => {
    setSession(null);
  }, []);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <KpiExplainContext.Provider value={value}>
      {children}
      <GlobalSidebar isOpen={session !== null} onClose={close}>
        {session ? <KpiExplainContent title={session.title} explain={session.explain} /> : null}
      </GlobalSidebar>
    </KpiExplainContext.Provider>
  );
}

export type { KpiExplain };
